import { useState, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  InputNumber,
  Tag,
  notification,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { IoCloseSharp } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { actions } from "../slice";

const { Title, Text } = Typography;

function AddStickers({
  KitDetail,
  fetchKitDetail,
  setKitDataAfterAssetCreation,
  setIsOpenModalAfterAssetCreation,
  disabledActions,
}) {
  const dispatch = useDispatch();
  const [isAddStickerModalVisible, setIsAddStickerModalVisible] =
    useState(false);
  const [isStickersAdding, setIsStickersAdding] = useState(false);

  const [startStickerRange, setStartStickerRange] = useState("");
  const [endStickerRange, setEndStickerRange] = useState("");
  const [manualSticker, setManualSticker] = useState("");
  const [stickers, setStickers] = useState([]);
  const [customPrefix, setCustomPrefix] = useState("");

  // Check if kit has existing stickers
  const hasExistingStickers = KitDetail?.sampleStickers?.length > 0;

  // Extract prefix from existing stickers or use custom prefix
  const prefix = hasExistingStickers
    ? KitDetail?.sampleStickers[0]?.sampleStickerNumber?.includes("-")
      ? KitDetail?.sampleStickers[0]?.sampleStickerNumber
          ?.split("-")
          ?.slice(0, -1)
          ?.join("-")
      : ""
    : customPrefix.trim();

  // Generate Stickers
  const handleGenerateStickers = () => {
    if (
      !startStickerRange ||
      !endStickerRange ||
      isNaN(startStickerRange) ||
      isNaN(endStickerRange) ||
      Number(startStickerRange) < 0 ||
      Number(endStickerRange) < 0
    ) {
      message.error("Please enter valid sticker numbers");
      return;
    }

    if (
      String(startStickerRange).length > 5 ||
      String(endStickerRange).length > 5
    ) {
      message.error("Maximum 5 digits are allowed");
      return;
    }

    const start = parseInt(startStickerRange, 10);
    const end = parseInt(endStickerRange, 10);
    if (start > end) {
      message.error("Start should be less than end");
      return;
    }
    if (end - start > 99) {
      message.error("Maximum 100 Stickers can be generated at a time");
      return;
    }

    const newStickers = [];
    for (let i = start; i <= end; i++) {
      const numericPart = String(i).padStart(5, "0");
      const sticker = prefix ? `${prefix}-${numericPart}` : numericPart;
      if (!stickers.includes(sticker)) {
        newStickers.push(sticker);
      }
    }

    if (newStickers?.length === 0) {
      message.info("No new unique stickers to add");
      return;
    }
    setStickers([...stickers, ...newStickers]);
  };

  // Add manual sticker
  const handleAddManualSticker = () => {
    if (!manualSticker || isNaN(manualSticker) || Number(manualSticker) < 0) {
      message.error("Please enter valid sticker number");
      return;
    }

    if (String(manualSticker).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }

    const numericPart = String(manualSticker).padStart(5, "0");
    const newSticker = prefix ? `${prefix}-${numericPart}` : numericPart;
    if (stickers.includes(newSticker)) {
      message.warning("Sticker No. already exists");
      return;
    }
    setStickers([...stickers, newSticker]);
    setManualSticker("");
  };

  // Delete sticker
  const handleDeleteSticker = (sticker) => {
    setStickers(stickers.filter((s) => s !== sticker));
  };

  const handleAddStickers = () => {
    if (stickers.length === 0) {
      message.error("Please generate or add at least one sticker");
      return;
    }

    const data = {
      sampleStickers: stickers,
      samplerKitId: KitDetail._id,
    };
    setIsStickersAdding(true);
    dispatch(
      actions.addSticker({
        data,
        onSuccess: (data) => {
          setKitDataAfterAssetCreation(data.data);
          resetState();
          setIsOpenModalAfterAssetCreation(true);
          fetchKitDetail();
        },
        onFailure: (error) => {
          setIsStickersAdding(false);

          let errorMessage = "Failed to Add Stickers";
          if (error?.data?.failedStickerNumbers?.length === stickers?.length) {
            errorMessage =
              "Oops, These sticker ids already taken, Please make unique ids";
          }
          notification.error({
            message: errorMessage || error.message,
          });
        },
      })
    );
  };

  const resetState = () => {
    setIsAddStickerModalVisible(false);
    setStartStickerRange("");
    setEndStickerRange("");
    setManualSticker("");
    setStickers([]);
    setCustomPrefix("");
  };

  // Update sticker numbers when prefix changes (only when no existing stickers)
  useEffect(() => {
    if (hasExistingStickers || stickers.length === 0) return;

    const updatedStickers = stickers.map((sticker) => {
      const numericPart = sticker.includes("-")
        ? sticker.split("-").pop()
        : sticker;
      return customPrefix.trim()
        ? `${customPrefix.trim()}-${numericPart}`
        : numericPart;
    });

    setStickers(updatedStickers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPrefix, hasExistingStickers]);

  return (
    <>
      <Tooltip
        title={
          disabledActions
            ? "Sticker can only be added when the kit is in the Pending or Packed stage"
            : "Add more stickers to this kit"
        }
      >
        <Button
          type="primary"
          onClick={() => {
            setIsAddStickerModalVisible(true);
          }}
          disabled={disabledActions}
        >
          Add More Stickers
        </Button>
      </Tooltip>
      <Modal
        visible={isAddStickerModalVisible}
        onCancel={resetState}
        title={
          <Row align="middle" justify="space-between" style={{ width: "100%" }}>
            <Title level={3} style={{ fontWeight: 800, margin: 0, padding: 0 }}>
              🏷️ Add More Stickers
            </Title>
            {!hasExistingStickers && (
              <Space align="center">
                <Text style={{ fontWeight: 600, color: "#666", fontSize: 14 }}>
                  Prefix:
                </Text>
                <input
                  type="text"
                  value={customPrefix}
                  onChange={(e) =>
                    setCustomPrefix(e.target.value.toUpperCase())
                  }
                  placeholder="e.g., AG-ST"
                  style={{
                    width: 150,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #d9d9d9",
                    fontWeight: 600,
                    fontSize: 14,
                    marginRight: 32,
                  }}
                />
              </Space>
            )}
          </Row>
        }
        footer={
          <Space>
            <Button
              key="cancel"
              onClick={resetState}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={isStickersAdding}
              onClick={handleAddStickers}
              disabled={stickers.length === 0}
              style={{
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Add Stickers ({stickers.length})
            </Button>
          </Space>
        }
        width={800}
        bodyStyle={{ borderRadius: 16, padding: 24, background: "#fff" }}
        style={{ borderRadius: 16 }}
        closeIcon={<IoCloseSharp size="24" />}
        centered
        className="modal-content"
      >
        <div>
          <Text style={{ fontWeight: 600, color: "#232A36" }}>
            Sticker Range
          </Text>
          <Row gutter={8} style={{ marginTop: 6, marginBottom: 16 }}>
            <Col span={9}>
              <InputNumber
                value={startStickerRange}
                onChange={(value) => setStartStickerRange(value)}
                placeholder="Start"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
              />
            </Col>
            <Col span={9}>
              <InputNumber
                value={endStickerRange}
                onChange={(value) => setEndStickerRange(value)}
                placeholder="End"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
              />
            </Col>
            <Col span={6}>
              <Button
                type="primary"
                onClick={handleGenerateStickers}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  fontWeight: 700,
                }}
              >
                Generate
              </Button>
            </Col>
          </Row>

          {/* Add Manual Sticker */}
          <Text style={{ fontWeight: 600, color: "#232A36" }}>
            Add Manual Sticker
          </Text>
          <Row gutter={12} style={{ marginTop: 6, marginBottom: 15 }}>
            <Col span={18}>
              <InputNumber
                value={manualSticker}
                onChange={(value) => setManualSticker(value)}
                placeholder="Enter Numeric Value (e.g., 1)"
                style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
              />
            </Col>
            <Col span={6}>
              <Button
                type="primary"
                onClick={handleAddManualSticker}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  fontWeight: 700,
                }}
              >
                Add
              </Button>
            </Col>
          </Row>

          {/* Sticker IDs */}
          {stickers?.length ? (
            <Card
              style={{
                borderRadius: 15,
                border: "1px solid #E5E8EC",
                background: "#FAFAFA",
              }}
            >
              <Row
                justify="space-between"
                align="middle"
                style={{ marginBottom: 12 }}
              >
                <Text
                  style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}
                >
                  Sticker IDs
                </Text>
                <Tag
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    background: "#E5E8EC",
                    borderRadius: 6,
                    padding: "4px 12px",
                    border: "none",
                  }}
                >
                  {stickers.length} {`Sticker${stickers.length > 1 ? "s" : ""}`}
                </Tag>
              </Row>
              <Row
                gutter={[12, 12]}
                style={{
                  border: "1px solid #E5E8EC",
                  padding: "12px",
                  borderRadius: 12,
                  overflow: "auto",
                  maxHeight: 220,
                }}
              >
                {stickers.map((sticker, idx) => (
                  <Col span={8} key={sticker + idx}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #E5E8EC",
                        padding: "4px 12px",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#232A36",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span>{sticker}</span>
                      <Tooltip title="Delete">
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteSticker(sticker)}
                          style={{ color: "#E14D4F", fontSize: 16 }}
                        />
                      </Tooltip>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

export default AddStickers;
